<template>
  <PageShell title="Reports" subtitle="Sales Channel • platform performance • AOV">
    <template #actions>
      <button class="ll-pill ghost" type="button" @click="resetFilters">Reset</button>
      <button class="ll-pill" type="button" @click="fetchReport" :disabled="loading">
        {{ loading ? "Generating…" : "Generate" }}
      </button>
    </template>

    <section class="card">
      <div class="card-top">
        <div>
          <div class="card-title">Date Range</div>
          <div class="micro subtle">Use chips for quick ranges.</div>
        </div>

        <div class="micro dim">
          Platforms: <b>{{ fmtInt(rows.length) }}</b>
        </div>
      </div>

      <div class="filters">
        <label class="field">
          <span class="k">Start</span>
          <input type="date" v-model="startDate" class="input" />
        </label>

        <label class="field">
          <span class="k">End</span>
          <input type="date" v-model="endDate" class="input" />
        </label>
      </div>

      <div class="range-row">
        <button
          v-for="r in ranges"
          :key="r.key"
          class="range-chip"
          type="button"
          :class="rangeKey === r.key ? 'on' : ''"
          @click="applyRange(r.key)"
        >
          {{ r.label }}
        </button>

        <div class="range-spacer"></div>

        <div class="snapshot">
          <div class="snap">
            <div class="k">Orders</div>
            <div class="v mono">{{ fmtInt(totals.orders) }}</div>
          </div>
          <div class="snap">
            <div class="k">Sales</div>
            <div class="v mono">${{ fmtMoney(totals.sales) }}</div>
          </div>
          <div class="snap">
            <div class="k">AOV</div>
            <div class="v mono">${{ fmtMoney(totals.aov) }}</div>
          </div>
        </div>
      </div>
    </section>

    <section class="card empty" v-if="!loading && rows.length === 0">
      <div class="empty-title">No report data</div>
      <div class="empty-sub">Pick a date range and click Generate.</div>
    </section>

    <section class="card" v-if="loading">
      <div class="skeleton-table">
        <div class="sk-row" v-for="n in 6" :key="n">
          <div class="sk-bar w35"></div>
          <div class="sk-bar w15"></div>
          <div class="sk-bar w15"></div>
          <div class="sk-bar w15"></div>
        </div>
      </div>
    </section>

    <section class="card" v-if="!loading && rows.length">
      <div class="table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th class="left">Platform</th>
              <th class="right">Orders</th>
              <th class="right">Sales</th>
              <th class="right">Avg Order Value</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="p in sortedRows" :key="p.platform">
              <td class="left">
                <div class="row-title">
                  <span class="strong">{{ p.platform || "—" }}</span>
                  <span class="chip sm" :class="rankChip(p).cls">{{ rankChip(p).label }}</span>
                </div>
              </td>
              <td class="right mono">{{ fmtInt(p.total_orders) }}</td>
              <td class="right mono">${{ fmtMoney(p.total_sales) }}</td>
              <td class="right mono">${{ fmtMoney(p.avg_order_value) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </PageShell>
</template>

<script>
import api from "@/services/api";
import PageShell from "@/components/layout/PageShell.vue";

export default {
  name: "SalesChannelReport",
  components: { PageShell },

  data() {
    return {
      loading: false,
      startDate: "",
      endDate: "",
      rangeKey: "30d",
      reportData: { sales_by_platform: [] },

      ranges: [
        { key: "7d", label: "7D" },
        { key: "30d", label: "30D" },
        { key: "90d", label: "90D" },
        { key: "ytd", label: "YTD" },
        { key: "all", label: "ALL" },
      ],
    };
  },

  computed: {
    rows() {
      return Array.isArray(this.reportData?.sales_by_platform)
        ? this.reportData.sales_by_platform
        : [];
    },

    sortedRows() {
      return [...this.rows].sort((a, b) => Number(b?.total_sales || 0) - Number(a?.total_sales || 0));
    },

    totals() {
      const rows = this.rows;
      const orders = rows.reduce((s, r) => s + Number(r?.total_orders || 0), 0);
      const sales = rows.reduce((s, r) => s + Number(r?.total_sales || 0), 0);
      const aov = orders > 0 ? sales / orders : 0;
      return { orders, sales, aov };
    },
  },

  mounted() {
    this.applyRange("30d");
  },

  methods: {
    applyRange(key) {
      this.rangeKey = key;

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const toISODate = (d) => d.toISOString().slice(0, 10);

      if (key === "all") {
        this.startDate = "";
        this.endDate = "";
        return;
      }

      if (key === "ytd") {
        this.startDate = toISODate(new Date(today.getFullYear(), 0, 1));
        this.endDate = toISODate(today);
        return;
      }

      const days = key === "7d" ? 7 : key === "30d" ? 30 : 90;
      const start = new Date(today);
      start.setDate(start.getDate() - (days - 1));

      this.startDate = toISODate(start);
      this.endDate = toISODate(today);
    },

    resetFilters() {
      this.applyRange("30d");
      this.reportData = { sales_by_platform: [] };
    },

    async fetchReport() {
      try {
        this.loading = true;
        const res = await api.get("/reports/sales-channel", {
          params: { start_date: this.startDate || "", end_date: this.endDate || "" },
        });
        this.reportData = res.data || { sales_by_platform: [] };
      } catch (e) {
        console.error("Error fetching Sales Channel Report:", e);
        this.reportData = { sales_by_platform: [] };
      } finally {
        this.loading = false;
      }
    },

    rankChip(p) {
      const sales = Number(p?.total_sales || 0);
      if (sales >= 5000) return { cls: "hot", label: "🔥 Big" };
      if (sales >= 1500) return { cls: "good", label: "👍 Solid" };
      if (sales >= 300) return { cls: "meh", label: "🫤 Small" };
      return { cls: "neutral", label: "—" };
    },

    fmtMoney(n) {
      const x = Number(n || 0);
      return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(x);
    },
    fmtInt(n) {
      const x = Number(n || 0);
      return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(x);
    },
  },
};
</script>

<style scoped>
.card {
  border-radius: 16px;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
  padding: 14px;
  margin-bottom: 14px;
}
.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
}
.card-title { font-weight: 900; font-size: 16px; }
.micro { font-size: 12px; font-weight: 700; }
.subtle { opacity: 0.8; }
.dim { opacity: 0.7; }

.filters {
  display: grid;
  grid-template-columns: 220px 220px;
  gap: 10px;
  align-items: end;
}
.field .k {
  display: block;
  font-size: 12px;
  font-weight: 900;
  opacity: 0.7;
  margin-bottom: 6px;
}
.input {
  width: 100%;
  padding: 12px 12px;
  border-radius: 14px;
  border: 1px solid rgba(176, 196, 222, 0.95);
  background: #f6fbff;
  font-weight: 800;
  font-size: 14px;
}

.range-row {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 12px;
}
.range-chip {
  border: 1px solid rgba(176, 196, 222, 0.95);
  background: rgba(246, 251, 255, 0.9);
  padding: 8px 10px;
  border-radius: 999px;
  font-weight: 900;
  cursor: pointer;
}
.range-chip.on {
  border-color: rgba(40, 134, 113, 0.55);
  box-shadow: 0 6px 16px rgba(40, 134, 113, 0.12);
}
.range-spacer { flex: 1; }

.snapshot {
  display: grid;
  grid-template-columns: repeat(3, minmax(120px, 1fr));
  gap: 10px;
}
.snap {
  background: #f6fbff;
  border: 1px solid rgba(176, 196, 222, 0.75);
  border-radius: 14px;
  padding: 10px;
}
.snap .k { font-weight: 900; font-size: 12px; opacity: 0.7; }
.snap .v { font-weight: 900; font-size: 14px; margin-top: 4px; }

.table-wrap { overflow: auto; }
.table {
  width: 100%;
  border-collapse: collapse;
  min-width: 820px;
}
.table th, .table td {
  border: 1px solid rgba(131, 167, 203, 0.55);
  padding: 10px 8px;
  background: rgba(255,255,255,0.65);
}
.table thead th {
  position: sticky;
  top: 0;
  background: #eef4fb;
  z-index: 3;
}

.left { text-align: left; }
.right { text-align: right; }

.row-title { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.strong { font-weight: 900; }
.mono {
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 12px;
  border: 1px solid rgba(176, 196, 222, 0.75);
  background: rgba(246, 251, 255, 0.9);
}
.chip.sm { padding: 5px 9px; }
.chip.hot { border-color: rgba(255, 140, 0, 0.45); }
.chip.good { border-color: rgba(40, 134, 113, 0.45); }
.chip.meh { opacity: 0.9; }
.chip.neutral { opacity: 0.75; }

.empty { text-align: center; }
.empty-title { font-weight: 900; font-size: 18px; }
.empty-sub { margin-top: 6px; opacity: 0.8; font-weight: 800; }

.skeleton-table { display: grid; gap: 10px; }
.sk-row { display: grid; grid-template-columns: 1fr repeat(3, 0.6fr); gap: 10px; }
.sk-bar {
  height: 12px;
  border-radius: 999px;
  background: rgba(176, 196, 222, 0.55);
  position: relative;
  overflow: hidden;
}
.sk-bar::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0) 100%);
  animation: shimmer 1.2s infinite;
}
@keyframes shimmer { 100% { transform: translateX(100%); } }
.w35 { width: 35%; } .w15 { width: 15%; }

@media (max-width: 980px) {
  .filters { grid-template-columns: 1fr; }
  .snapshot { grid-template-columns: 1fr; width: 100%; }
}
</style>
